import { useEffect, useState } from 'react';

export function useDataGridLocalStorage(
  apiRef,
  storageKey = 'service-table-settings'
) {
  const savedSettings = JSON.parse(localStorage.getItem(storageKey) || '{}');

  const [filterModel, setFilterModel] = useState(
    savedSettings.filterModel || {
      items: [],
      logicOperator: 'and',
      quickFilterValues: [],
      quickFilterLogicOperator: 'and',
    }
  );

  const [paginationModel, setPaginationModel] = useState(
    savedSettings.paginationModel || { page: 0, pageSize: 50 }
  );

  const [sortModel, setSortModel] = useState(
    savedSettings.sortModel || [{ field: 'id', sort: 'desc' }]
  );

  const [columnOrder, setColumnOrder] = useState(savedSettings.columnOrder || []);
  const [columnSizes, setColumnSizes] = useState(savedSettings.columnSizes || {});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(savedSettings.columnVisibilityModel || {}
  );

  // Guardar en localStorage cuando cambian modelos
  useEffect(() => {
    const toSave = {
      sortModel,
      filterModel,
      paginationModel,
      columnOrder,
      columnSizes,
      columnVisibilityModel,
    };

    localStorage.setItem(storageKey, JSON.stringify(toSave));
  }, [sortModel, filterModel, paginationModel, columnOrder, columnSizes, columnVisibilityModel]);

  // Escuchar cambios en columnas y guardar
  useEffect(() => {
    if (!apiRef?.current) return;

    const handleColumnChange = () => {
      const state = apiRef.current.exportState();
      const currentSettings = JSON.parse(localStorage.getItem(storageKey) || '{}');

      const updated = {
        ...currentSettings,
        columnOrder: state.columns?.orderedFields,
        columnSizes: state.columns?.dimensions,
      };

      localStorage.setItem(storageKey, JSON.stringify(updated));
      setColumnOrder(state.columns?.orderedFields || []);
      setColumnSizes(state.columns?.dimensions || {});
    };

    const unsubscribeOrder = apiRef.current.subscribeEvent('columnOrderChange', handleColumnChange);
    const unsubscribeWidth = apiRef.current.subscribeEvent('columnWidthChange', handleColumnChange);

    return () => {
      unsubscribeOrder();
      unsubscribeWidth();
    };
  }, [apiRef, storageKey]);

  // Restaurar orden/tamaño de columnas una sola vez
  useEffect(() => {
    if (!apiRef?.current || !savedSettings.columnOrder || !savedSettings.columnSizes) return;

    const tryRestore = () => {
      const columnsReady = apiRef.current?.getAllColumns?.().length > 0;
      if (columnsReady && apiRef.current.restoreState) {
        apiRef.current.restoreState({
          columns: {
            orderedFields: savedSettings.columnOrder,
            dimensions: savedSettings.columnSizes,
          },
        });
        return true;
      }
      return false;
    };

    const interval = setInterval(() => {
      if (tryRestore()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [apiRef, savedSettings.columnOrder, savedSettings.columnSizes]);

  const resetSettings = () => {
    localStorage.removeItem(storageKey);
    setFilterModel({
      items: [],
      logicOperator: 'and',
      quickFilterValues: [],
      quickFilterLogicOperator: 'and',
    });
    setPaginationModel({ page: 0, pageSize: 50 });
    setSortModel([{ field: 'id', sort: 'desc' }]);
    setColumnOrder([]);
    setColumnSizes({});
    setColumnVisibilityModel({});
  };

  return {
    filterModel,
    setFilterModel,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    columnOrder,
    columnSizes,
    columnVisibilityModel,
    setColumnVisibilityModel,
    resetSettings,
  };
}
